/**
 * `GoalCards` — the three "goals" strip under the hero. Each card is a
 * link/scroll target to its section. Monochrome by default; the only
 * colored marks are inherited from the next-step card (not here).
 */

import type { AgentOverviewResponse } from '@sill/types/api';
import type { ReadinessSectionTally } from '@sill/types';

import { useC } from '../../lib/theme.js';
import { Kicker } from './MonoLink.js';
import { monoFont, sansFont } from './fonts.js';

export interface GoalCardsProps {
  readonly discoverable: ReadinessSectionTally;
  readonly discoverable_sentence: string;
  readonly policies: ReadinessSectionTally;
  readonly policies_sentence: string;
  readonly overview: AgentOverviewResponse | null;
  readonly scriptInstalled: boolean;
  readonly onNavigate: (section: 'discoverable' | 'policies' | 'activity') => void;
  /** When false, counts and progress bars are suppressed (— of —, flat
   *  bar) and the fallback sentence is used until the first scan runs. */
  readonly has_snapshot?: boolean;
}

export function GoalCards({
  discoverable, discoverable_sentence,
  policies, policies_sentence,
  overview, scriptInstalled, onNavigate,
  has_snapshot = true,
}: GoalCardsProps): JSX.Element {
  const C = useC();

  const cardStyle = {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 4,
    padding: 24,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 14,
    cursor: 'pointer',
    textAlign: 'left' as const,
    fontFamily: sansFont,
    minHeight: 44,
  };

  // Visitor-mix bar — monochrome ramp (text / muted / dim). People
  // segment hidden when the site hasn't been running the script.
  const mix = overview?.mix;
  const total_mix = mix
    ? mix.totals.ai.traffic + mix.totals.bots.traffic + (scriptInstalled ? mix.totals.humans.traffic : 0)
    : 0;
  const ai_pct = mix && total_mix > 0 ? Math.round((mix.totals.ai.traffic / total_mix) * 100) : null;
  const bots_pct = mix && total_mix > 0 ? Math.round((mix.totals.bots.traffic / total_mix) * 100) : null;
  const humans_pct = scriptInstalled && mix && total_mix > 0 ? Math.round((mix.totals.humans.traffic / total_mix) * 100) : null;
  const total_visits_label = total_mix > 0 ? total_mix.toLocaleString('en-US') + ' visits' : '— visits';

  return (
    <section aria-label="Goals" className="grid grid-cols-1 md:grid-cols-3 gap-5">
      <button type="button" onClick={() => onNavigate('discoverable')} style={cardStyle} data-testid="goal-discoverable">
        <div className="flex items-baseline justify-between gap-3">
          <Kicker>Discoverable</Kicker>
          <span style={{ fontFamily: monoFont, fontSize: 12, color: C.body }}>
            {!has_snapshot || discoverable.counted === 0 ? '— of —' : `${discoverable.passing} of ${discoverable.counted}`}
          </span>
        </div>
        <div style={{ height: 4, borderRadius: 2, background: C.surface2, overflow: 'hidden' }}>
          <div style={{ height: 4, background: C.body, width: `${!has_snapshot || discoverable.counted === 0 ? 0 : (discoverable.passing / discoverable.counted) * 100}%` }} />
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.55, color: C.body }}>
          {!has_snapshot ? 'Not checked yet.' : discoverable_sentence}
        </div>
      </button>

      <button type="button" onClick={() => onNavigate('policies')} style={cardStyle} data-testid="goal-policies">
        <div className="flex items-baseline justify-between gap-3">
          <Kicker>Policies &amp; records</Kicker>
          <span style={{ fontFamily: monoFont, fontSize: 12, color: C.body }}>
            {!has_snapshot || policies.counted === 0 ? '— of —' : `${policies.passing} of ${policies.counted}`}
          </span>
        </div>
        <div style={{ height: 4, borderRadius: 2, background: C.surface2, overflow: 'hidden' }}>
          <div style={{ height: 4, background: C.body, width: `${!has_snapshot || policies.counted === 0 ? 0 : (policies.passing / policies.counted) * 100}%` }} />
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.55, color: C.body }}>
          {!has_snapshot ? 'Not checked yet.' : policies_sentence}
        </div>
      </button>

      <button type="button" onClick={() => onNavigate('activity')} style={cardStyle} data-testid="goal-activity">
        <div className="flex items-baseline justify-between gap-3">
          <Kicker>Agent activity</Kicker>
          <span style={{ fontFamily: monoFont, fontSize: 12, color: C.body }}>{total_visits_label}</span>
        </div>
        {total_mix === 0 ? (
          <div style={{ height: 8, borderRadius: 2, background: C.surface2 }} aria-hidden="true" />
        ) : (
          <div className="flex" style={{ height: 8, borderRadius: 2, overflow: 'hidden', gap: 2 }}>
            {ai_pct !== null ? <div style={{ width: `${ai_pct}%`, background: C.text }} /> : null}
            {bots_pct !== null ? <div style={{ width: `${bots_pct}%`, background: C.muted }} /> : null}
            {humans_pct !== null ? <div style={{ width: `${humans_pct}%`, background: C.dim }} /> : null}
          </div>
        )}
        <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
          <span style={{ color: C.text }}>AI agents {ai_pct ?? '—'}%</span>
          {' · '}
          <span>Other bots {bots_pct ?? '—'}%</span>
          {scriptInstalled ? <>{' · '}<span>People {humans_pct ?? '—'}%</span></> : null}
        </div>
        {!scriptInstalled ? (
          <div style={{ fontSize: 12, color: C.dim }}>People are counted only where the Sill script runs.</div>
        ) : null}
      </button>
    </section>
  );
}
